import {
  ContentCharge,
  Environment,
  SysEnv,
  Viewport
} from "@/src/types/digitalData";

/**
 * Detects viewport size category
 * @param width - Viewport width in pixels
 * @returns Viewport category, defaults to "mobile" for invalid values
 */
export const getViewport = (width: number): Viewport => {
  // Validate width parameter for edge cases
  if (!Number.isFinite(width) || width <= 0) {
    return "mobile";
  }

  if (width < 768) {
    return "mobile";
  }
  if (width < 1024) {
    return "tablet";
  }
  if (width < 1200) {
    return "desktop";
  }
  if (width < 1920) {
    return "desktop-wide";
  }
  return "full";
};

/**
 * Detects system environment (mobile/tablet/desktop) from user agent.
 * Tablet is reported separately so analytics can distinguish phone vs tablet vs desktop traffic.
 */
export const getSysEnv = (): SysEnv => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isTablet = /ipad|android(?!.*mobile)/.test(userAgent);
  const isMobile =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(
      userAgent
    );

  if (isTablet) {
    return "tablet";
  }
  if (isMobile) {
    return "mobile";
  }
  return "desktop";
};

/**
 * Gets environment from env variable or defaults to dev
 */
export const getEnvironment = (): Environment => {
  const env = import.meta.env.VITE_BAANA_ENVIRONMENT?.toLowerCase();
  if (
    env === "prod" ||
    env === "qa" ||
    env === "test" ||
    env === "integration" ||
    env === "ci" ||
    env === "dev"
  ) {
    return env;
  }
  return "dev";
};

/**
 * Gets language code from locale
 */
export const getLanguageCode = (locale: string): string => {
  const localeMap: Record<string, string> = {
    fi: "fi-FI",
    en: "en-US"
  };
  return localeMap[locale] || "fi-FI";
};

/**
 * Gets content charge status (always free for Baana)
 * Note: Currently returns a constant value. Kept as a function for future extensibility
 * (e.g., paid content detection). Could be simplified to a constant if no changes are planned.
 */
export const getContentCharge = (): ContentCharge => {
  return "free";
};

/**
 * Gets initial locale from localStorage (same as localeAtom) or falls back to navigator.language
 * Handles SecurityError when localStorage access is blocked (e.g., private/incognito mode,
 * cross-origin iframes) and JSON parsing errors by falling through to navigator.language
 */
export const getInitialLocale = (): string => {
  try {
    const storedLocale = localStorage.getItem("localeAtom");
    if (storedLocale) {
      const parsed = JSON.parse(storedLocale);
      if (parsed === "fi" || parsed === "en") {
        return parsed;
      }
    }
  } catch (error) {
    // Handle SecurityError (blocked localStorage) or JSON parse errors
    // Fall through to navigator.language fallback
  }

  // Fallback to navigator.language, extract first part (e.g., "fi-FI" -> "fi")
  if (navigator.language) {
    const langCode = navigator.language.split("-")[0].toLowerCase();
    if (langCode === "fi" || langCode === "en") {
      return langCode;
    }
  }

  // Final fallback
  return "fi";
};
